import React, {Dispatch, useEffect, useState} from 'react';
import {
    EditorEvent,
    EditorEventType,
    EditorLayersMode,
    EditorTool,
    EditorToolType,
    ExtendedEditorToolDefinition,
    LayerId,
    ViewerToolType,
} from '@mc/react-mapio';
import {Card, Divider, Space} from '@mc/ui-v4';
import './Toolbar.scss';
import * as IconSpec from '@mc/react-ui-icons';
import {ToolbarDetail} from './ToolbarDetail';
import {ToolbarButton} from './ToolbarButton';
import {getToolInfo} from '../../helpers/ToolsServiceMapper';
import {ETriggerAction, Trigger} from '../../helpers/Trigger';
import {useMapioContext} from '../../mapioContext';
import {newItemToolbarChildDef, ToolbarGroupId} from './ToolbarConst';
import {handleToolAction} from './ToolActions';
import {sendEvent} from '../../helpers/EventUtils';
import {useAppConfig} from '@mc/react-app-base';
import {useIsMobile} from '../../hooks/useIsMobile';
import {findToolKeyById, findTopLevelParentKey} from '../../helpers/ToolUtils';

type Props = {
    setOtherToolsTrigger: Dispatch<React.SetStateAction<Trigger>>;
    snappingActive: boolean;
    toolsEnabled: EditorToolType[];
    layersMode: EditorLayersMode;
    allowedInsertionLayers?: LayerId[];
};
export const Toolbar: React.FC<Props> = ({setOtherToolsTrigger, snappingActive, toolsEnabled, layersMode, allowedInsertionLayers = []}) => {
    // @ts-ignore
    const {serverPublic} = useAppConfig();
    const isMobile = useIsMobile();
    const mapioContext = useMapioContext();
    const {mapRef, selectedDetailFeature, disabled, activeTool, currentTools, topicId, writableLayers, insertionLayerId} = mapioContext.value;
    const [selectedToolKey, setSelectedToolKey] = useState<number>(null);
    const selectedTool: ExtendedEditorToolDefinition = getToolInfo(currentTools[selectedToolKey]);

    useEffect(() => {
        // Pokud zavřeme v informačním menu nástroj, který byl aktivní,
        // tak je potřeba jej deaktivovat i zde
        if (
            selectedTool?.type !== activeTool &&
            selectedTool?.id !== ToolbarGroupId.SELECT_MENU &&
            activeTool !== EditorToolType.MEASURE_LINE &&
            activeTool !== EditorToolType.MEASURE_POLYGON &&
            selectedTool?.id !== ToolbarGroupId.MENU_NEW
        ) {
            setSelectedToolKey(null);
        }
    }, [topicId, activeTool]);

    useEffect(() => {
        const foundToolKey = findTopLevelParentKey(currentTools, activeTool);
        if (foundToolKey === null) {
            let foundToolKeyById = findToolKeyById(currentTools, ToolbarGroupId.MENU_NEW);
            if (foundToolKeyById !== null && newItemToolbarChildDef.includes(activeTool as any)) {
                setSelectedToolKey(prevKey => (prevKey === foundToolKeyById ? prevKey : foundToolKeyById));
            } else {
                setSelectedToolKey(prevKey => (prevKey === null ? prevKey : null));
            }
        } else {
            setSelectedToolKey(prevKey => (prevKey === foundToolKey ? prevKey : foundToolKey));
        }
    }, [toolsEnabled, currentTools, activeTool]);

    // Deaktivace insert nástroje řešíme MIMO render (jinak smyčka re-renderů).
    // Deaktivujeme POUZE když byla vypnuta právě ta vrstva, do které vkládáme – tzn. insertionLayerId
    // zmizel z writableLayers mapy. Rozhodujeme podle writableLayers (stabilní stav z mapy), NIKOLIV podle
    // allowedInsertionLayers: to se počítá z opravneniQuery, který se při změně writableLayers refetchuje
    // a během načítání je dočasně prázdný → jinak by se insert deaktivoval i při vypnutí jiné vrstvy.
    useEffect(() => {
        const isInsertTool = activeTool === EditorToolType.POINT || activeTool === EditorToolType.LINE || activeTool === EditorToolType.POLYGON;
        const insertionLayerGone = insertionLayerId != null && !(writableLayers ?? []).includes(insertionLayerId);
        if (mapRef?.current && isInsertTool && insertionLayerGone && layersMode !== EditorLayersMode.OVERLAY) {
            setSelectedToolKey(null);
            mapRef.current?.activateToolWithType(EditorToolType.EXACT_SELECT);
        }
    }, [JSON.stringify(writableLayers), insertionLayerId, activeTool, layersMode, mapRef?.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const isActive = tool => {
        if (tool.type === EditorToolType.SNAP) {
            return snappingActive;
        } else if (mapioContext.value.isIframe) {
            return activeTool === tool.type;
        } else if (
            ([EditorToolType.EXACT_SELECT, EditorToolType.BOX_SELECT, EditorToolType.POLY_SELECT, EditorToolType.COPY].includes(activeTool) &&
                tool.id === ToolbarGroupId.SELECT_MENU) ||
            ([EditorToolType.MEASURE_LINE, EditorToolType.MEASURE_POLYGON].includes(activeTool) && tool.id === ToolbarGroupId.MEASURE_MENU)
        ) {
            return true;
        } else {
            return selectedToolKey && selectedTool?.id ? selectedTool.id === tool.id : selectedTool?.type === tool?.type;
        }
    };

    if (currentTools.length === 0) {
        return null;
    }
    return (
        <Space className={`position-absolute toolbar-wrapper ${isMobile && serverPublic ? 'mobile' : ''}`}>
            <Card className={'px-1 toolbar card-border drop-shadow'} mode={'view'}>
                <Space vertical className={'py-1'}>
                    {currentTools.map((tool, key) => {
                        const toolInfo = getToolInfo(tool);
                        if (toolInfo.id === ToolbarGroupId.MENU_NEW && !allowedInsertionLayers.length) {
                            // Pokud uživatel nemá právo zapisovat, či není zrovna aktivní
                            // zapisovatelná vrstva, nástroj pro insert skryjeme.
                            // Deaktivaci aktivního insert nástroje řeší useEffect níže (MIMO render) –
                            // dříve se setSelectedToolKey + activateToolWithType volaly přímo v renderu,
                            // což způsobovalo "Too many re-renders" / "update MapEditor while rendering Toolbar".
                            return false;
                        }
                        return toolInfo === EditorTool.SEPARATOR ? (
                            <Divider key={`${tool.toString}_${key}`} type={'line'} horizontal />
                        ) : (
                            <ToolbarButton
                                colorEdge={!toolInfo.horizontal && toolInfo.type === EditorToolType.MENU}
                                disabled={
                                    disabled ||
                                    (EditorToolType.MENU !== toolInfo.type &&
                                        EditorToolType.CUSTOM_ACTION !== toolInfo.type &&
                                        !Object.values(ViewerToolType).includes(toolInfo.type) &&
                                        !toolsEnabled?.includes(toolInfo.type))
                                }
                                key={`${typeof tool === 'object' ? tool.id : tool}_${key}`}
                                title={toolInfo.name}
                                size={'lg'}
                                onClick={() => {
                                    handleToolAction({
                                        toolType: toolInfo.type,
                                        mapRef,
                                        selectedDetailFeature,
                                    });
                                    if (toolInfo.type === EditorToolType.SHOW_COORDS || toolInfo.type === EditorToolType.EXPORT_LAYERS) {
                                        setOtherToolsTrigger({
                                            actionToTrigger: ETriggerAction.SHOW,
                                            tool: toolInfo.type,
                                        });
                                    }
                                    if (toolInfo.type === EditorToolType.CUSTOM_ACTION) {
                                        if (mapioContext.value.isIframe) {
                                            // Vlastní akci si řeší nadřazená aplikace, pouze jí předáme ID nástroje
                                            sendEvent({type: EditorEventType.CUSTOM_ACTION, toolId: toolInfo.id} as EditorEvent, mapioContext.value.instanceId);
                                        } else {
                                            mapRef.current.activateToolWithId(toolInfo.id);
                                        }
                                        return;
                                    }
                                    if (selectedToolKey === key) {
                                        setSelectedToolKey(null);
                                        // Pokud zavřeme menu s vybraným nástrojem, ten se deaktivuje a aktivuje se EXACT_SELECT
                                        if (
                                            activeTool === EditorToolType.SHOW_COORDS ||
                                            // @ts-ignore
                                            tool.items?.some(item => item?.items?.includes(activeTool)) ||
                                            newItemToolbarChildDef?.includes(activeTool)
                                        ) {
                                            console.log('<<<< Deactivating menu tool, activating EXACT_SELECT');
                                            mapRef.current.activateToolWithType(EditorToolType.EXACT_SELECT);
                                        }
                                    } else {
                                        if (![EditorTool.SNAP, EditorTool.ORIGIN_LOCATION].includes(toolInfo.type)) {
                                            setSelectedToolKey(key);
                                        }

                                        if (toolInfo.type !== EditorToolType.MENU) {
                                            mapRef.current.activateToolWithType(toolInfo.type);
                                        }
                                    }
                                }}
                                active={isActive(toolInfo)}
                                icon={IconSpec[isActive(toolInfo) && toolInfo.iconActive ? toolInfo.iconActive : toolInfo.icon]}
                                intent={toolInfo.type === EditorTool.SNAP && !isActive(toolInfo) ? 'inactive' : 'primary'}
                            />
                        );
                    })}
                </Space>
            </Card>
            {selectedToolKey != null && selectedToolKey >= 0 && selectedTool?.type === EditorToolType.MENU && (
                <ToolbarDetail
                    activeTool={activeTool}
                    toolsEnabled={toolsEnabled}
                    disabled={disabled}
                    mapRef={mapRef}
                    tool={selectedTool}
                    setSelectedToolKey={setSelectedToolKey}
                    horizontal={selectedTool.horizontal}
                    writableLayers={allowedInsertionLayers}
                    selectedDetailFeature={selectedDetailFeature}
                    setOtherToolsTrigger={setOtherToolsTrigger}
                    layersMode={layersMode}
                />
            )}
        </Space>
    );
};
